import { configureStore, Tuple } from '@reduxjs/toolkit'
import { combineReducers, Reducer } from 'redux'
import { persistStore, persistReducer, createTransform } from 'redux-persist'
import customMiddleware from './customMiddleware'
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import { thunk } from 'redux-thunk'
import { Post } from 'types/post'
import activityReducer, { ActivityReducer } from './activityReducer';
import alertReducer, { AlertReducer } from './alertReducer';
import communityReducer, { CommunityReducer } from './communityReducer';
import eventReducer, { EventReducer } from './eventReducer';
import fetchReducer, { FetchReducer } from './fetchReducer';
import interestReducer, { InterestReducer } from './interestReducer';
import mainReducer, { MainReducer } from './mainReducer';
import postReducer, { PostReducer } from './postReducer';
import profileReducer, { ProfileReducer } from './profileReducer';
import userReducer, { UserReducer } from './userReducer';
import { Event } from 'types/event';
import { Community } from 'types/community';
import { isDev } from 'app/general';
import { PublicProfile } from 'types/publicProfile';

const createNoopStorage = () => {
  return {
    getItem(_key: any) {
      return Promise.resolve(null);
    },
    setItem(_key: any, value: any) {
      return Promise.resolve(value);
    },
    removeItem(_key: any) {
      return Promise.resolve();
    },
  };
};

const postsTransformer = createTransform(
  (inboundState: PostReducer, key) => {
    return {
      ...inboundState,
      posts: Array.from(inboundState.posts).map(([_, post]: [any, Post]) => post),
    };
  },
  (outboundState, key) => {
    return {
      ...outboundState,
      posts: new Map(outboundState.posts.map((post: Post) => [post.id, post]))
    };
  },
  { whitelist: ['postReducer'] }
);

const eventsTransformer = createTransform(
  (inboundState: EventReducer, key) => {
    return {
      ...inboundState,
      events: Array.from(inboundState.events).map(([_, event]: [any, Event]) => event),
    };
  },
  (outboundState, key) => {
    return {
      ...outboundState,
      events: new Map(outboundState.events.map((event: Event) => [event.id, event]))
    };
  },
  { whitelist: ['eventReducer'] }
);


const communitiesTransformer = createTransform(
  (inboundState: CommunityReducer, key) => {
    return {
      ...inboundState,
      communities: Array.from(inboundState.communities).map(([_, community]: [any, Community]) => community),
    };
  },
  (outboundState, key) => {
    return {
      ...outboundState,
      communities: new Map(outboundState.communities.map((community: Community) => [community.id, community]))
    };
  },
  { whitelist: ['communityReducer'] }
);

const publicProfilesTransformer = createTransform(
  (inboundState: ProfileReducer, key) => {
    return {
      ...inboundState,
      publicProfiles: Array.from(inboundState.publicProfiles).map(([_, publicProfile]: [any, PublicProfile]) => publicProfile),
    };
  },
  (outboundState, key) => {
    return {
      ...outboundState,
      publicProfiles: new Map(outboundState.publicProfiles.map((publicProfile: PublicProfile) => [publicProfile.id, publicProfile]))
    };
  },
  { whitelist: ['profileReducer'] }
);

const storage = typeof window !== "undefined" ? createWebStorage("local") : createNoopStorage();

const persistConfig = {
  key: 'root',
  storage,
  transforms: [postsTransformer, eventsTransformer, communitiesTransformer, publicProfilesTransformer],
  blacklist: ['mainReducer', 'interestReducer'],
}


const rootReducer = combineReducers({
  interestReducer,
  eventReducer,
  fetchReducer,
  alertReducer,
  mainReducer,
  profileReducer,
  activityReducer,
  postReducer,
  communityReducer,
  userReducer
})

const middleware = new Tuple(thunk, customMiddleware)

if (isDev) {
  const { createLogger } = require('redux-logger');

  const logger = createLogger({
    duration: true,
    timestamp: false,
    collapsed: true,
    colors: {
      title: () => '#139BFE',
      prevState: () => '#1C5FAF',
      action: () => '#149945',
      nextState: () => '#A47104',
      error: () => '#ff0005',
    },
    predicate: () => process.browser,
  });

  middleware.push(logger);
}

type RootReducer = Reducer<{
  interestReducer: InterestReducer,
  eventReducer: EventReducer,
  fetchReducer: FetchReducer,
  alertReducer: AlertReducer,
  mainReducer: MainReducer,
  profileReducer: ProfileReducer,
  activityReducer: ActivityReducer,
  postReducer: PostReducer,
  communityReducer: CommunityReducer,
  userReducer: UserReducer
}>

const persistedReducer = persistReducer(persistConfig, rootReducer as RootReducer);
const store = configureStore({
  reducer: persistedReducer,
  middleware: () => middleware,
});

const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export default () => {
  return { store, persistor }
}